import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiX, HiCheck, HiPlay, HiOutlinePhotograph } from 'react-icons/hi';
import './styles/MediaManagement.css';
import './styles/Modal.css';
import { mediaService } from '../../services/mediaService';

function MediaManagement({ selectedEdition }) {
  const [mediaData, setMediaData] = useState([]);
  const [mediaImages, setMediaImages] = useState([]);
  const [socialImages, setSocialImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalType, setModalType] = useState('video'); // 'video', 'image', 'social'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [activeTab, setActiveTab] = useState('videos');

  // NEW: duplicate detection state
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  // NEW: prevent multiple bulk deletes
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadAllMediaData();
    }
  }, [selectedEdition]);

  const loadAllMediaData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      
      // Load videos
      const videos = await mediaService.getMediaData(selectedEdition);
      setMediaData(videos || []);

      // Load media images
      const images = await mediaService.getMediaImages(selectedEdition);
      setMediaImages(images || []);

      // Load social media images
      const socialImgs = await mediaService.getSocialMediaImages(selectedEdition);
      setSocialImages(socialImgs || []);

      const isEmpty = (!videos || videos.length === 0) && 
                     (!images || images.length === 0) && 
                     (!socialImgs || socialImgs.length === 0);
      setIsDatabaseEmpty(isEmpty);
    } catch (error) {
      console.error('Error loading media data:', error);
      setIsDatabaseEmpty(true);
      setMediaData([]);
      setMediaImages([]);
      setSocialImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVideo = (video) => {
    setSelectedItem(video);
    setModalMode('edit');
    setModalType('video');
    setShowModal(true);
  };

  const handleDeleteVideo = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette vidéo ?')) {
      try {
        await mediaService.deleteVideo(id);
        setMediaData(prev => prev.filter(video => video.id !== id));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('Erreur lors de la suppression de la vidéo.');
      }
    }
  };

  const handleAddVideo = () => {
    setSelectedItem(null);
    setModalMode('add');
    setModalType('video');
    setShowModal(true);
  };

  const handleAddMediaImage = () => {
    setSelectedItem(null);
    setModalMode('add');
    setModalType('image');
    setShowModal(true);
  };

  const handleAddSocialImage = () => {
    setSelectedItem(null);
    setModalMode('add');
    setModalType('social');
    setShowModal(true);
  };

  const handleEditImage = (image, type) => {
    setSelectedItem(image);
    setModalMode('edit');
    setModalType(type);
    setShowModal(true);
  };

  const handleDeleteImage = async (id, type) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette image ?')) {
      try {
        if (type === 'image') {
          await mediaService.deleteMediaImage(id);
          setMediaImages(prev => prev.filter(img => img.id !== id));
        } else {
          await mediaService.deleteSocialMediaImage(id);
          setSocialImages(prev => prev.filter(img => img.id !== id));
        }
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Erreur lors de la suppression de l\'image.');
      }
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalType === 'video') {
        if (modalMode === 'add') {
          const newVideo = await mediaService.addVideo(itemData, selectedEdition);
          const formattedVideo = {
            id: newVideo.id,
            title: newVideo.title,
            description: newVideo.description,
            thumbnail: newVideo.thumbnail_url,
            thumbnailPath: newVideo.thumbnail_path,
            videoUrl: newVideo.video_url,
            duration: newVideo.duration,
            displayOrder: newVideo.display_order,
            persons: newVideo.persons || [] // NEW: include persons
          };
          setMediaData(prev => [...prev, formattedVideo]);
        } else {
          await mediaService.updateVideo(selectedItem.id, itemData);
          setMediaData(prev => prev.map(video =>
            video.id === selectedItem.id ? { ...video, ...itemData } : video
          ));
        }
      } else if (modalType === 'image') {
        if (modalMode === 'add') {
          // Support multiple images uploaded at once
          if (itemData.images && itemData.images.length > 0) {
            const createdImages = [];
            for (const img of itemData.images) {
              // Create payload per image. Use provided title/description or fallback to file name.
              const payload = {
                title: itemData.title || img.originalName || '',
                description: itemData.description || '',
                imageUrl: img.url,
                imagePath: img.path,
                displayOrder: itemData.displayOrder || 0,
                persons: img.persons || itemData.persons || [] // NEW: attach persons from img or form
              };
              const newImage = await mediaService.addMediaImage(payload, selectedEdition);
              const formattedImage = {
                id: newImage.id,
                title: newImage.title,
                description: newImage.description,
                imageUrl: newImage.image_url,
                imagePath: newImage.image_path,
                displayOrder: newImage.display_order,
                persons: newImage.persons || [] // NEW: include persons returned from DB
              };
              createdImages.push(formattedImage);
            }
            // Append all newly created images to state
            setMediaImages(prev => [...prev, ...createdImages]);
          } else {
            // fallback to single image (existing behavior)
            const payload = {
              ...itemData,
              persons: itemData.persons || []
            };
            const newImage = await mediaService.addMediaImage(payload, selectedEdition);
            const formattedImage = {
              id: newImage.id,
              title: newImage.title,
              description: newImage.description,
              imageUrl: newImage.image_url,
              imagePath: newImage.image_path,
              displayOrder: newImage.display_order,
              persons: newImage.persons || []
            };
            setMediaImages(prev => [...prev, formattedImage]);
          }
        } else {
          // EDIT image: persist persons and other fields
          const updated = await mediaService.updateMediaImage(selectedItem.id, {
            title: itemData.title,
            description: itemData.description,
            imageUrl: itemData.imageUrl || selectedItem.imageUrl,
            imagePath: itemData.imagePath || selectedItem.imagePath,
            persons: itemData.persons || (selectedItem.persons || [])
          });
          setMediaImages(prev => prev.map(img =>
            img.id === selectedItem.id ? {
              ...img,
              title: updated.title,
              description: updated.description,
              imageUrl: updated.image_url,
              imagePath: updated.image_path,
              persons: updated.persons || []
            } : img
          ));
        }
      } else if (modalType === 'social') {
        if (modalMode === 'add') {
          const payload = {
            ...itemData,
            persons: itemData.persons || [] // NEW: include persons
          };
          const newSocialImage = await mediaService.addSocialMediaImage(payload, selectedEdition);
          const formattedSocialImage = {
            id: newSocialImage.id,
            title: newSocialImage.title,
            description: newSocialImage.description,
            imageUrl: newSocialImage.image_url,
            imagePath: newSocialImage.image_path,
            socialPlatform: newSocialImage.social_platform,
            displayOrder: newSocialImage.display_order,
            persons: newSocialImage.persons || [] // NEW: include persons
          };
          setSocialImages(prev => [...prev, formattedSocialImage]);
        } else {
          // EDIT social image: persist persons and other fields
          const updated = await mediaService.updateSocialMediaImage(selectedItem.id, {
            title: itemData.title,
            description: itemData.description,
            imageUrl: itemData.imageUrl || selectedItem.imageUrl,
            imagePath: itemData.imagePath || selectedItem.imagePath,
            socialPlatform: itemData.socialPlatform || selectedItem.socialPlatform,
            persons: itemData.persons || (selectedItem.persons || [])
          });
          setSocialImages(prev => prev.map(img =>
            img.id === selectedItem.id ? {
              ...img,
              title: updated.title,
              description: updated.description,
              imageUrl: updated.image_url,
              imagePath: updated.image_path,
              socialPlatform: updated.social_platform,
              persons: updated.persons || []
            } : img
          ));
        }
      }

      setIsDatabaseEmpty(false);
      closeModal();
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setModalMode('add');
    setModalType('video');
  };

  // NEW: normalize url/path into a stable key (remove query, lowercase, take filename)
  const normalizeKey = (raw) => {
    if (!raw) return '';
    try {
      // If it's a full URL, use pathname + filename without query
      if (raw.startsWith('http')) {
        const u = new URL(raw);
        const path = u.pathname || '';
        const name = path.split('/').filter(Boolean).pop() || path;
        return name.toLowerCase();
      }
      // If it contains separators, take last segment
      const cleaned = raw.split(/[\\/]/).pop();
      // remove query strings if present
      return (cleaned || '').split('?')[0].toLowerCase();
    } catch (e) {
      // fallback: strip query part and lowercase
      return raw.split('?')[0].toLowerCase();
    }
  };

  // NEW: find duplicate images across gallery, social and video thumbnails
  const findDuplicateImages = () => {
    const map = new Map();
    // collect gallery images
    mediaImages.forEach(img => {
      const key = normalizeKey(img.imagePath || img.imageUrl || img.image_path || img.image_url);
      const entry = { id: img.id, type: 'image', title: img.title, url: img.imageUrl || img.image_url, path: img.imagePath || img.image_path };
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });
    // collect social images
    socialImages.forEach(img => {
      const key = normalizeKey(img.imagePath || img.imageUrl || img.image_path || img.image_url);
      const entry = { id: img.id, type: 'social', title: img.title, url: img.imageUrl || img.image_url, path: img.imagePath || img.image_path, socialPlatform: img.socialPlatform || img.social_platform };
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });
    // collect video thumbnails
    mediaData.forEach(v => {
      const key = normalizeKey(v.thumbnailPath || v.thumbnail || v.thumbnail_path || v.thumbnail_url || v.videoUrl);
      const entry = { id: v.id, type: 'video', title: v.title, url: v.thumbnail || v.thumbnail_url || v.videoUrl, path: v.thumbnailPath || v.thumbnail_path };
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });

    // filter groups with more than one item
    const groups = [];
    for (const [key, items] of map.entries()) {
      // ignore empty keys
      if (!key) continue;
      if (items.length > 1) {
        groups.push({ key, items });
      }
    }

    setDuplicateGroups(groups);
    setShowDuplicatesModal(true);
    // also log to console for quick inspection
    if (groups.length === 0) {
      console.info('No duplicate image groups found.');
    } else {
      console.info('Duplicate image groups:', groups);
    }
  };

  // NEW: delete helper that reuses existing handlers and refreshes groups
  const deleteDuplicateItem = async (item) => {
    try {
      if (item.type === 'video') {
        await handleDeleteVideo(item.id);
      } else {
        await handleDeleteImage(item.id, item.type);
      }
    } catch (err) {
      console.error('Error deleting duplicate item:', err);
    } finally {
      // refresh duplicate detection after removal
      // slight delay to ensure state updated after underlying delete handlers
      setTimeout(() => findDuplicateImages(), 300);
    }
  };

  // NEW: delete all duplicates at once, keep first item of each group
  const deleteAllDuplicates = async () => {
    if (!duplicateGroups || duplicateGroups.length === 0) return;
    if (!window.confirm('Supprimer tous les éléments en double (un seul conservé par groupe) ?')) return;
    setDeletingDuplicates(true);
    try {
      for (const group of duplicateGroups) {
        // keep first item and delete the rest
        const [keep, ...toDelete] = group.items;
        for (const it of toDelete) {
          try {
            if (it.type === 'video') {
              await mediaService.deleteVideo(it.id);
            } else if (it.type === 'image') {
              await mediaService.deleteMediaImage(it.id);
            } else if (it.type === 'social') {
              await mediaService.deleteSocialMediaImage(it.id);
            }
          } catch (err) {
            console.warn(`Failed to delete ${it.type} id=${it.id}:`, err);
          }
        }
      }
      // refresh all lists from server
      await loadAllMediaData();
      // recompute duplicates
      findDuplicateImages();
      showSuccessMessage();
      // close modal after success
      setShowDuplicatesModal(false);
    } catch (err) {
      console.error('Error deleting all duplicates:', err);
      alert('Erreur lors de la suppression des doublons.');
    } finally {
      setDeletingDuplicates(false);
    }
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>Aucun média n'a été ajouté pour cette édition.</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Opération effectuée avec succès !
        </div>
      )}

      {/* Tabs */}
      <div className="media-tabs">
        <button 
          className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Vidéos ({mediaData.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'images' ? 'active' : ''}`}
          onClick={() => setActiveTab('images')}
        >
          Images Galerie ({mediaImages.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'social' ? 'active' : ''}`}
          onClick={() => setActiveTab('social')}
        >
          Images Réseaux Sociaux ({socialImages.length})
        </button>

        {/* NEW: Duplicate check action */}
        <button className="tab-button find-duplicates-btn" style={{ marginLeft: 12 }} onClick={findDuplicateImages}>
          Vérifier les doublons
        </button>
      </div>

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>Gestion des Vidéos</h3>
            <button className="add-btn" onClick={handleAddVideo}>
              <HiPlus /> Ajouter une vidéo
            </button>
          </div>
          
          {mediaData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>Aucune vidéo disponible.</p>
            </div>
          ) : (
            <div className="videos-management-grid">
              {mediaData.map(video => (
                <div key={video.id} className="video-management-card">
                  <div className="video-thumbnail-container">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgdmlld0JveD0iMCAwIDQwMCAyMjUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjI1IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xODAgMTAwTDIyMCAxMjVMMTgwIDE1MFYxMDBaIiBmaWxsPSIjOUM5Qzk5Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTgwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5WaWTDqW8gYWZmaWNowqllPC90ZXh0Pgo8L3N2Zz4K';
                      }}
                    />
                    <div className="video-overlay">
                      <HiPlay className="play-icon" />
                      <span className="duration">{video.duration}</span>
                    </div>
                  </div>
                  <div className="video-info">
                    <h4>{video.title}</h4>
                    <p>{video.description}</p>
                    <div className="video-actions">
                      <button onClick={() => handleEditVideo(video)} className="btn-edit">
                        <HiPencil /> Modifier
                      </button>
                      <button onClick={() => handleDeleteVideo(video.id)} className="btn-delete">
                        <HiTrash /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>Gestion des Images de Galerie</h3>
            <button className="add-btn" onClick={handleAddMediaImage}>
              <HiPlus /> Ajouter une image
            </button>
          </div>
          
          {mediaImages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>Aucune image disponible.</p>
            </div>
          ) : (
            <div className="images-management-grid">
              {mediaImages.map(image => (
                <div key={image.id} className="image-management-card">
                  <div className="image-container">
                    <img 
                      src={image.imageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUM5Qzk5Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTYwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD4KPC9zdmc+'} 
                      alt={image.title}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUM5Qzk5Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTYwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD4KPC9zdmc+';
                      }}
                    />
                  </div>
                  <div className="image-info">
                    <h4>{image.title}</h4>
                    <p>{image.description}</p>
                    <div className="image-actions">
                      <button onClick={() => handleEditImage(image, 'image')} className="btn-edit">
                        <HiPencil /> Modifier
                      </button>
                      <button onClick={() => handleDeleteImage(image.id, 'image')} className="btn-delete">
                        <HiTrash /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Social Images Tab */}
      {activeTab === 'social' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>Gestion des Images Réseaux Sociaux</h3>
            <button className="add-btn" onClick={handleAddSocialImage}>
              <HiPlus /> Ajouter une image
            </button>
          </div>
          
          {socialImages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>Aucune image de réseau social disponible.</p>
            </div>
          ) : (
            <div className="images-management-grid">
              {socialImages.map(image => (
                <div key={image.id} className="image-management-card">
                  <div className="image-container">
                    <img 
                      src={image.imageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUM5Qzk5Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTYwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD4KPC9zdmc>'} 
                      alt={image.title}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUM5Qzk5Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTYwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD4KPC9zdmc>';
                      }}
                    />
                    {image.socialPlatform && (
                      <div className="social-platform-badge">{image.socialPlatform}</div>
                    )}
                  </div>
                  <div className="image-info">
                    <h4>{image.title}</h4>
                    <p>{image.description}</p>
                    <div className="image-actions">
                      <button onClick={() => handleEditImage(image, 'social')} className="btn-edit">
                        <HiPencil /> Modifier
                      </button>
                      <button onClick={() => handleDeleteImage(image.id, 'social')} className="btn-delete">
                        <HiTrash /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal for duplicates */}
      {showDuplicatesModal && (
        <div className="modal-overlay" onClick={() => setShowDuplicatesModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Doublons d'images détectés ({duplicateGroups.length})</h2>
              <button className="modal-close-btn" onClick={() => setShowDuplicatesModal(false)}>
                <HiX />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {duplicateGroups.length === 0 ? (
                <p>Aucun doublon trouvé.</p>
              ) : (
                duplicateGroups.map((group, gi) => (
                  <div key={gi} style={{ borderBottom: '1px solid #eee', padding: '10px 0' }}>
                    <div style={{ fontSize: 13, color: '#555' }}>Groupe: <strong>{group.key}</strong> — {group.items.length} éléments</div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                      {group.items.map(it => (
                        <div key={`${it.type}-${it.id}`} style={{ width: 220, border: '1px solid #ddd', borderRadius: 6, padding: 8, position: 'relative' }}>
                          <div style={{ height: 100, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                            {it.url ? <img src={it.url} alt={it.title} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} onError={(e)=>{ e.target.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCI+PC9zdmc+' }} /> : <div style={{ color: '#999' }}>Pas d'aperçu</div>}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{it.title || '(sans titre)'}</div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{it.type}{it.socialPlatform ? ' • ' + it.socialPlatform : ''}</div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" className="btn-delete" onClick={() => deleteDuplicateItem(it)}>
                              <HiTrash /> Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowDuplicatesModal(false); }}>
                Fermer
              </button>
              <button 
                className="btn-primary" 
                onClick={deleteAllDuplicates} 
                disabled={deletingDuplicates || duplicateGroups.length === 0}
                style={{ marginLeft: 8 }}
              >
                {deletingDuplicates ? 'Suppression en cours...' : 'Supprimer tous les doublons'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <MediaModal
          mode={modalMode}
          type={modalType}
          item={selectedItem}
          onSave={handleSaveItem}
          onClose={closeModal}
          selectedEdition={selectedEdition}
        />
      )}
    </div>
  );
}

function MediaModal({ mode, type, item, onSave, onClose, selectedEdition }) {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    description: item?.description || '',
    imageUrl: item?.imageUrl || '',
    imagePath: item?.imagePath || '',
    socialPlatform: item?.socialPlatform || '',
    videoUrl: item?.videoUrl || '',
    duration: item?.duration || '',
    thumbnail: item?.thumbnail || '',
    thumbnailPath: item?.thumbnailPath || '',
    persons: (item && (item.persons || item.people || item.personnes)) ? (Array.isArray(item.persons) ? item.persons.join(', ') : String(item.persons)) : ''
  });
  const [imagePreview, setImagePreview] = useState(item?.imageUrl || item?.thumbnail || '');
  const [uploadingImage, setUploadingImage] = useState(false);

  // New state: keep track of multiple uploaded images (for gallery)
  const [uploadedImages, setUploadedImages] = useState(() => {
    if (item && (type === 'image')) {
      return [{ url: item.imageUrl || item.thumbnail || '', path: item.imagePath || item.thumbnailPath || '', originalName: item.title || '', persons: item.persons || [] }];
    }
    return [];
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // For videos we keep single-thumbnail behavior
    if (type === 'video') {
      const file = files[0];
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('L\'image est trop volumineuse. Taille maximum: 5MB.');
        return;
      }
      try {
        setUploadingImage(true);
        const uploadResult = await mediaService.uploadVideoThumbnail(file, selectedEdition);
        setFormData(prev => ({ 
          ...prev, 
          thumbnail: uploadResult.url,
          thumbnailPath: uploadResult.path
        }));
        setImagePreview(uploadResult.url);
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        alert('Erreur lors du téléchargement de la miniature.');
      } finally {
        setUploadingImage(false);
      }
      return;
    }

    // For gallery images: allow multiple uploads
    const newUploaded = [];
    try {
      setUploadingImage(true);
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          // skip invalid files
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          // skip too large files
          continue;
        }
        const uploadResult = await mediaService.uploadMediaImage(file, selectedEdition);
        newUploaded.push({
          url: uploadResult.url,
          path: uploadResult.path,
          originalName: file.name,
          persons: [] // will be set from formData.persons on save
        });
      }
      if (newUploaded.length > 0) {
        setUploadedImages(prev => [...prev, ...newUploaded]);
        // set the preview to first uploaded for immediate visual feedback
        setImagePreview(newUploaded[0].url);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Erreur lors du téléchargement des images.');
    } finally {
      setUploadingImage(false);
      // reset file input value so same files can be selected again if needed
      e.target.value = '';
    }
  };

  const removeUploadedImage = async (index) => {
    const img = uploadedImages[index];
    if (!img) return;
    try {
      // attempt to delete remote file to avoid orphaned uploads
      if (img.path) {
        await mediaService.deleteImageFile(img.path);
      }
    } catch (err) {
      console.warn('Could not delete image file:', err);
    } finally {
      setUploadedImages(prev => prev.filter((_, i) => i !== index));
      // adjust preview
      const remaining = uploadedImages.filter((_, i) => i !== index);
      setImagePreview(remaining.length ? remaining[0].url : '');
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ 
      ...prev, 
      imageUrl: url,
      imagePath: '' // Clear path since this is a URL
    }));
    setImagePreview(url);
  };

  const convertToEmbedUrl = (url) => {
    // Convert various YouTube URL formats to embed format
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtube.com/embed/')) {
      return url; // Already in embed format
    }
    return url; // Return original URL for other platforms
  };

  const handleVideoUrlChange = (e) => {
    const url = e.target.value;
    const embedUrl = convertToEmbedUrl(url);
    setFormData({ ...formData, videoUrl: embedUrl });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build payload. For gallery images include uploadedImages array if present.
    const payload = { ...formData };
    if (type === 'image' && uploadedImages.length > 0) {
      // parse persons string into array and attach to each uploaded image
      const personsArr = (formData.persons || '').split(',').map(s => s.trim()).filter(Boolean);
      payload.images = uploadedImages.map(img => ({
        url: img.url,
        path: img.path,
        originalName: img.originalName,
        persons: personsArr
      })); // parent will handle creating many entries
    } else {
      // if single image or social, include persons field too
      payload.persons = (formData.persons || '').split(',').map(s => s.trim()).filter(Boolean);
    }
    onSave(payload);
  };

  const getModalTitle = () => {
    const action = mode === 'add' ? 'Ajouter' : 'Modifier';
    if (type === 'video') return `${action} une Vidéo`;
    if (type === 'image') return `${action} une Image de Galerie`;
    if (type === 'social') return `${action} une Image de Réseau Social`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getModalTitle()}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-group">
              <label>Titre</label>
              <input 
                type="text" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Entrez le titre"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Entrez la description"
                required
                rows="3"
              />
            </div>

            {type === 'video' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>URL de la vidéo</label>
                    <input 
                      type="url" 
                      value={formData.videoUrl}
                      onChange={handleVideoUrlChange}
                      placeholder="https://www.youtube.com/watch?v=..."
                      required
                    />
                    <p className="url-hint">
                      Formats acceptés: youtube.com/watch?v=..., youtu.be/..., ou youtube.com/embed/...
                      <br />
                      L'URL sera automatiquement convertie au format d'intégration.
                    </p>
                  </div>
                  
                  <div className="form-group">
                    <label>Durée</label>
                    <input 
                      type="text" 
                      value={formData.duration}
                      onChange={(e) => setFormData({...formData, duration: e.target.value})}
                      placeholder="ex: 45:30"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Miniature de la vidéo</label>
                </div>
              </>
            )}

            {type === 'social' && (
              <>
                <div className="form-group">
                  <label>Plateforme sociale</label>
                  <select 
                    value={formData.socialPlatform}
                    onChange={(e) => setFormData({...formData, socialPlatform: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner une plateforme</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Twitter">Twitter</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="YouTube">YouTube</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>URL de l'image</label>
                  <input 
                    type="url" 
                    value={formData.imageUrl}
                    onChange={handleImageUrlChange}
                    placeholder="https://example.com/image.jpg"
                    required
                  />
                  <p className="url-hint">
                    Copiez l'URL directe de l'image depuis le réseau social ou utilisez un lien d'image publique.
                  </p>
                </div>
              </>
            )}

            {type === 'image' && (
              <div className="form-group">
                <label>Image</label>
              </div>
            )}

            {type !== 'social' && (
              <div className="image-upload-container">
                <div className="image-preview-container">
                  {/* If multiple uploaded previews exist, show grid */}
                  {uploadedImages.length > 0 ? (
                    <div className="multi-preview-grid">
                      {uploadedImages.map((img, idx) => (
                        <div key={idx} className="multi-preview-item">
                          <img src={img.url} alt={img.originalName || `upload-${idx}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6 }} onError={(e) => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCI+PC9zdmc+' }} />
                          <button type="button" className="remove-upload-btn" onClick={() => removeUploadedImage(idx)} title="Retirer">
                            <HiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="image-preview"
                      style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+Cjwvc3ZnPg==';
                      }}
                    />
                  ) : (
                    <div className="image-placeholder" style={{ width: '200px', height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: '8px', border: '2px dashed #ccc' }}>
                      <HiOutlinePhotograph style={{ fontSize: '30px', color: '#666', marginBottom: '8px' }} />
                      <span style={{ color: '#666', fontSize: '14px' }}>Aucune image</span>
                    </div>
                  )}
                </div>

                <div className="upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="image-upload"
                    className="file-input"
                    disabled={uploadingImage}
                    // allow multiple files only for gallery images
                    multiple={type === 'image' ? true : undefined}
                  />
                  <label htmlFor="image-upload" className={`upload-btn ${uploadingImage ? 'uploading' : ''}`}>
                    <HiOutlinePhotograph />
                    {uploadingImage ? 'Téléchargement...' : (type === 'image' ? 'Choisir des images' : 'Choisir une image')}
                  </label>
                  <p className="upload-hint">JPG, PNG ou GIF (max. 5MB)</p>
                </div>
              </div>
            )}

            {type === 'social' && (
              <div className="social-image-preview">
                <div className="preview-label">Aperçu de l'image:</div>
                <div className="social-preview-container">
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt="Social media preview" 
                      className="social-image-preview-img"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCA2MEgxMjBWMTQwSDgwVjYwWiIgZmlsbD0iIzlDOUM5OSIvPgo8dGV4dCB4PSIxMDAiIHk9IjE3MCIgZm9udC1mYW1pbHk9InN5c3RlbS11aSIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzlDOUM5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgZXJyZXVyPC90ZXh0Pgo8L3N2Zz4K';
                      }}
                    />
                  ) : (
                    <div className="social-placeholder">
                      <HiOutlinePhotograph style={{ fontSize: '24px', color: '#999' }} />
                      <span>L'aperçu apparaîtra ici</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Personnes (séparées par des virgules)</label>
              <input
                type="text"
                value={formData.persons}
                onChange={(e) => setFormData({...formData, persons: e.target.value})}
                placeholder="Ex: Jean Dupont, Marie Martin"
              />
              <p className="url-hint">Noms des personnes présentes sur l'image, séparés par des virgules.</p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={uploadingImage}>
              {mode === 'add' ? 'Ajouter' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MediaManagement;

